import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  WORK_AREA_COMPANY_TREE_FILTER_STORAGE_KEY,
  WORK_AREA_USER_COMPANIES_FILTER_STORAGE_KEY,
  WorkAreaFilterCategories,
} from 'src/constants/workAreaFilterCategories';
import useAuth from 'src/hooks/useAuth';
import useLocalStorage from 'src/hooks/useLocalstorage';
import useRedirect from 'src/hooks/useRedirect';
import useGetSuperAdminsQuery from 'src/services/client/useGetSuperAdminsQuery';
import { DropDownCategories } from 'src/types/generic';
import { isSessionClientId } from 'src/utils/authImpersonation';
import { createContext, useContext } from 'use-context-selector';
import { isSuperAdminWorkAreaSession } from './workAreaImpersonation';

export interface ImpersonateResult {
  canImpersonate: boolean;
  /** True when actively impersonating the session user (label: Impersonated). */
  isImpersonatedUser: boolean;
  handleImpersonate: () => void;
}

interface WorkAreaContextType {
  companyTreeFilter: DropDownCategories;
  setCompanyTreeFilter: React.Dispatch<React.SetStateAction<DropDownCategories>>;
  userCompaniesFilter: DropDownCategories;
  setUserCompaniesFilter: React.Dispatch<React.SetStateAction<DropDownCategories>>;
  selectedCompanyId: number | null;
  setSelectedCompanyId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedCompanyName: string | null;
  setSelectedCompanyName: React.Dispatch<React.SetStateAction<string | null>>;
  isImpersonating: boolean;
  impersonatedCompanyId: number | null;
  impersonatedClientId: number | null;
  sessionClientId: number | null;
  /** Highlights the row for the active session user (impersonated or logged-in self). */
  isImpersonatedClient: (clientId?: number | null) => boolean;
  getImpersonate: (clientId?: number | null, companyId?: number | null) => ImpersonateResult;
  superAdminCompanyIds: number[];
  isWorkAreaSuperAdminSession: boolean;
}

export const WorkAreaContext = createContext<WorkAreaContextType>({} as WorkAreaContextType);

interface WorkAreaContextProviderProps {
  children: ReactNode;
}

export const WorkAreaContextProvider: FC<WorkAreaContextProviderProps> = ({ children }) => {
  const {
    isSuperAdmin,
    loggedInCompanyId,
    isImpersonating,
    impersonatedCompanyId,
    impersonatedClientId,
    sessionClientId,
  } = useAuth();
  const { getUrl, redirect } = useRedirect();
  const { data: superAdminsData } = useGetSuperAdminsQuery();
  const superAdminCompanyIds = useMemo(
    () => superAdminsData?.map((sa) => sa.companyId) ?? [],
    [superAdminsData]
  );
  const isWorkAreaSuperAdminSession = useMemo(
    () => isSuperAdminWorkAreaSession(isSuperAdmin, loggedInCompanyId, superAdminCompanyIds),
    [isSuperAdmin, loggedInCompanyId, superAdminCompanyIds]
  );
  const [companyTreeFilter, setCompanyTreeFilter] = useLocalStorage<DropDownCategories>(
    WORK_AREA_COMPANY_TREE_FILTER_STORAGE_KEY,
    WorkAreaFilterCategories[0]
  );
  const [userCompaniesFilter, setUserCompaniesFilter] = useLocalStorage<DropDownCategories>(
    WORK_AREA_USER_COMPANIES_FILTER_STORAGE_KEY,
    WorkAreaFilterCategories[0]
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);

  const checkIsSessionClient = useCallback(
    (clientId?: number | null) => isSessionClientId(clientId, sessionClientId),
    [sessionClientId]
  );

  const getImpersonate = useCallback(
    (clientId?: number | null, companyId?: number | null): ImpersonateResult => {
      const isCurrentSessionUser = checkIsSessionClient(clientId);
      const isImpersonatedUser = isImpersonating && isCurrentSessionUser;
      const canImpersonate = clientId != null && companyId != null && !isCurrentSessionUser;
      const handleImpersonate = () => {
        if (!canImpersonate || clientId == null || companyId == null) return;
        const clientIdEncrypted = btoa(String(clientId));
        const url = getUrl('proxyUser')(clientIdEncrypted, String(clientId), String(companyId));
        redirect(url, true, false);
      };
      return { canImpersonate, isImpersonatedUser, handleImpersonate };
    },
    [checkIsSessionClient, getUrl, isImpersonating, redirect]
  );

  const value: WorkAreaContextType = useMemo(
    () => ({
      companyTreeFilter,
      setCompanyTreeFilter,
      userCompaniesFilter,
      setUserCompaniesFilter,
      selectedCompanyId,
      setSelectedCompanyId,
      selectedCompanyName,
      setSelectedCompanyName,
      isImpersonating,
      impersonatedCompanyId,
      impersonatedClientId,
      sessionClientId,
      isImpersonatedClient: checkIsSessionClient,
      getImpersonate,
      superAdminCompanyIds,
      isWorkAreaSuperAdminSession,
    }),
    [
      companyTreeFilter,
      setCompanyTreeFilter,
      userCompaniesFilter,
      setUserCompaniesFilter,
      selectedCompanyId,
      selectedCompanyName,
      isImpersonating,
      impersonatedCompanyId,
      impersonatedClientId,
      sessionClientId,
      checkIsSessionClient,
      getImpersonate,
      superAdminCompanyIds,
      isWorkAreaSuperAdminSession,
    ]
  );

  return <WorkAreaContext.Provider value={value}>{children}</WorkAreaContext.Provider>;
};

export const useWorkAreaContext = () => {
  const context = useContext(WorkAreaContext);
  if (!context) {
    throw new Error('useWorkAreaContext must be used within WorkAreaContextProvider');
  }
  return context;
};
